import React from "react";
import { P } from "../../atoms/paragraph/Paragraph.styled";
import { Flex, Modal } from "antd";
import { useAppContext } from "../../../contexts/useAppContext";
import facebook from "../../../images/facebook.svg";
import instagram from "../../../images/instagram.svg";
import tiktok from "../../../images/tiktok.svg";
import x from "../../../images/x.svg";
import youtube from "../../../images/youtube.svg";
import { useWindowWidth } from "../../../hooks/useWindowWidth";
import { ucfirst } from "../../../utils";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const headerStyle: React.CSSProperties = {
  height: "auto",
  paddingLeft: "8px",
  paddingRight: "8px",
  paddingTop: "20px",
  paddingBottom: "20px",
  maxWidth: "800px",
  width: "100%",
  background: "var(--color-surface)",
};

const icons: any = {
  facebook: facebook,
  instagram: instagram,
  tiktok: tiktok,
  x: x,
  youtube: youtube,
};
type TimeRange = { from: string; till: string };
type Schedule = { [key: string]: TimeRange[] };

const formatScheduleForClient = (schedule: Schedule, t: TFunction): any => {
  const dayNames: { [key: string]: string } = {
    monday: t("footerComponent.dayMonday"),
    tuesday: t("footerComponent.dayTuesday"),
    wednesday: t("footerComponent.dayWednesday"),
    thursday: t("footerComponent.dayThursday"),
    friday: t("footerComponent.dayFriday"),
    saturday: t("footerComponent.daySaturday"),
    sunday: t("footerComponent.daySunday"),
  };

  return Object.entries(schedule)
    .sort((a, b) => {
      return (
        Object.keys(dayNames).indexOf(a[0]) -
        Object.keys(dayNames).indexOf(b[0])
      );
    })
    .map(([day, timeRanges]) => {
      if (timeRanges.length === 0) {
        return (
          <Flex key={day} justify="space-between" style={{ marginBottom: "8px" }}>
            <P $size={"14"} $weight={"400"} $height={"20"}>{dayNames[day]}</P>
            <P $size={"14"} $weight={"400"} $height={"20"}>{t("footerComponent.closed")}</P>
          </Flex>
        );
      }

      return (
        <Flex key={day} vertical style={{ marginBottom: "8px" }}>
          {timeRanges.map((range, index) => (
            <Flex key={`${day}-${index}`} justify="space-between">
              <P $size={"14"} $weight={"400"} $height={"20"}>
                {index === 0 ? dayNames[day] : ""}
              </P>
              <P $size={"14"} $weight={"400"} $height={"20"}>
                {`${range.from} - ${range.till}`}
              </P>
            </Flex>
          ))}
        </Flex>
      );
    });
};

export const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { selectedBranch, currentPage, navigateTo } = useAppContext();
  const width = useWindowWidth();

  if ((currentPage.page !== "dishes" && currentPage.page !== "services") || !selectedBranch) return null;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        borderTop: "1px solid var(--color-border)",
      }}
    >
      <Flex style={headerStyle} justify="start" vertical gap={20}>
        <Flex justify="space-between" align="start" vertical={width < 700}>
          <Flex
            vertical
            gap={5}
            style={{
              marginBottom: "10px",
            }}
          >
            <P $size={"var(--font-size-body-large)"} $weight={"var(--font-weight-semibold)"} $height={"var(--line-height-body)"}>
              {selectedBranch?.name}
            </P>
            <P $size={"var(--font-size-body-small)"} $weight={"var(--font-weight-normal)"} $height={"var(--line-height-body)"}>
              {ucfirst(selectedBranch?.description)}
            </P>
            {Object.keys(selectedBranch?.openingHours || {}).length > 0 && (
              <P
                $size={"var(--font-size-body-medium)"}
                $weight={"var(--font-weight-semibold)"}
                $height={"var(--line-height-body)"}
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => {
                  Modal.info({
                    title: t("footerComponent.orderHoursTitle"),
                    closable: true,
                    closeIcon: true,
                    icon: null,
                    okButtonProps: { style: { display: "none" } },
                    content: (
                      <Flex vertical gap={0}>
                        {formatScheduleForClient(selectedBranch?.openingHours, t)}
                      </Flex>
                    ),
                  });
                }}
              >
                {t("footerComponent.orderHoursTitle")}
              </P>
            )}
          </Flex>
          <Flex justify="start" align="center" gap={5}>
            {selectedBranch?.socialMedia &&
              Object.keys(selectedBranch?.socialMedia || {}).map((key) => {
                return (
                  <a
                    href={selectedBranch?.socialMedia?.[key]}
                    key={selectedBranch?.socialMedia?.[key]}
                  >
                    <img src={icons[key]} width={30} />
                  </a>
                );
              })}
          </Flex>
        </Flex>

        <Flex>
          <P $size={"var(--font-size-body-medium)"} $weight={"var(--font-weight-normal)"} $height={"var(--line-height-body)"}>
            {t("footerComponent.promoBefore")}{" "}
            <a
              href="https://proste.menu/"
              style={{
                color: "var(--color-text)",
                textDecoration: "underline",
              }}
            >
              proste.menu
            </a>{" "}
            {t("footerComponent.promoAfter")}
          </P>
        </Flex>

        <Flex
          justify="space-between"
          align="center"
          style={{
            paddingBottom: "60px",
          }}
        >
          <Flex gap={8}>
            <span
              onClick={() => navigateTo({ page: 'website-policy' })}
              style={{
                color: "var(--color-text)",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              <P
                $size={"var(--font-size-body-small)"}
                $weight={"var(--font-weight-normal)"}
                $height={"var(--line-height-body)"}
              >
                {t("footerComponent.policyAndTerms")}
              </P>
            </span>
          </Flex>
          <div>
            <P $size={"var(--font-size-body-small)"} $weight={"var(--font-weight-normal)"} $height={"var(--line-height-body)"}>
              Created by{" "}
              <a
                href="https://proste.menu"
                style={{
                  color: "var(--color-text)",
                  textDecoration: "underline",
                }}
              >
                proste.menu
              </a>
            </P>
          </div>
        </Flex>
      </Flex>
    </div>
  );
};
